import { useEffect, useState } from "react";

import { isAPIError } from "@/core/services";
import { listAllProfissionais, periodoDaObra } from "@/modules/recursos";
import type { Profissional } from "@/modules/recursos";

export const RESPONSAVEIS_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os profissionais do Hub.";
export const RESPONSAVEIS_ERROR_MESSAGE = "Não foi possível carregar os profissionais.";

/** Opção do seletor de responsável: o valor guardado na ficha é o nome. */
export interface ResponsavelOpcao {
  value: string;
  label: string;
  email: string;
}

export interface ResponsaveisOpcoesState {
  opcoes: ResponsavelOpcao[];
  loading: boolean;
  errorMessage: string | null;
}

const INICIAL: ResponsaveisOpcoesState = { opcoes: [], loading: false, errorMessage: null };

/** Nome do profissional (ou o e-mail, sem nome); homônimos viram uma só opção. */
export function opcoesDosProfissionais(profissionais: Profissional[]): ResponsavelOpcao[] {
  const porNome = new Map<string, ResponsavelOpcao>();
  for (const profissional of profissionais) {
    const nome = profissional.nome.trim() || profissional.email.trim();
    if (nome && !porNome.has(nome)) {
      porNome.set(nome, { value: nome, label: nome, email: profissional.email });
    }
  }
  return [...porNome.values()];
}

/**
 * Profissionais da etapa Equipe como opções dos quatro responsáveis (HU 2684), no período da obra.
 * Sem permissão ou com falha a lista fica vazia com a mensagem; a ficha segue sem responsável.
 */
export function useResponsaveisOpcoes(inicio: string | null, fim: string | null) {
  const [state, setState] = useState<ResponsaveisOpcoesState>(INICIAL);

  useEffect(() => {
    if (!inicio || !fim) return undefined;

    const controller = new AbortController();
    setState({ ...INICIAL, loading: true });

    listAllProfissionais(periodoDaObra(inicio, fim), controller.signal)
      .then((profissionais) => {
        if (controller.signal.aborted) return;
        setState({
          opcoes: opcoesDosProfissionais(profissionais),
          loading: false,
          errorMessage: null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const proibido = isAPIError(error) && error.status === 403;
        setState({
          opcoes: [],
          loading: false,
          errorMessage: proibido ? RESPONSAVEIS_FORBIDDEN_MESSAGE : RESPONSAVEIS_ERROR_MESSAGE,
        });
      });

    return () => controller.abort();
  }, [inicio, fim]);

  return state;
}
